import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, BookOpen, ChevronRight, X, ExternalLink, File, ArrowUpRight } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { API_BASE_URL, authHeaders } from '../api/coworkApi';
import { avatarSrc } from '../avatarSrc';
import type { Agent } from '../types/cowork';

/* ─── Types ─── */

interface DiaryLink {
  label: string;
  url: string;
  type: 'page' | 'file' | 'external';
}

interface DiaryEntry {
  id: string;
  teamId: string;
  agentId: string;
  title: string;
  summary: string;
  content: string;
  links: DiaryLink[];
  sourceType?: 'task' | 'skill' | 'chat' | 'manual';
  sourceId?: string;
  date: string;
  createdAt: string;
}

interface GrowthDiaryPageProps {
  agents: Agent[];
  onBack: () => void;
}

/* ─── Helpers ─── */

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateStr === today.toISOString().slice(0, 10)) return 'Today';
  if (dateStr === yesterday.toISOString().slice(0, 10)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(isoStr: string): string {
  return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatFullDate(isoStr: string): string {
  return new Date(isoStr).toLocaleString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/* ─── Demo entries ─── */

const DEMO_ENTRIES: DiaryEntry[] = [
  {
    id: 'demo-1', teamId: 'demo', agentId: 'aria-content',
    title: 'SEO Content Strategy Finalized',
    summary: 'Identified 47 high-intent keyword clusters across 5 content pillars. Top opportunities: "sustainable beauty routines" (2.4K/mo) and "clean skincare guide" (1.8K/mo).',
    content: '## SEO Content Strategy: Completed\n\nAfter thorough keyword research and competitor gap analysis, I\'ve finalized our content strategy for Q2.\n\n### Key Findings\n- **47 keyword clusters** mapped to 5 content pillars\n- Highest opportunity: "sustainable beauty routines" (2,400 searches/mo, KD 28)\n- Quick wins available in "clean skincare" and "cruelty-free makeup" categories\n\n### Recommended Content Calendar\n1. Weekly long-form blog posts (1,800+ words)\n2. 2x per week social snippets from pillar content\n3. Monthly comparison guides targeting competitor keywords',
    links: [{ label: 'View Task', url: '/cowork?taskId=demo-1', type: 'page' }],
    sourceType: 'task', sourceId: 'demo-1',
    date: new Date().toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 2 * 3600000).toISOString(),
  },
  {
    id: 'demo-2', teamId: 'demo', agentId: 'maya-seo',
    title: 'Competitor Backlink Analysis: 3 Key Gaps Found',
    summary: 'Analyzed top 5 competitors. Found 3 high-authority domains linking to competitors but not us. Total link opportunity: ~180 referring domains.',
    content: '## Competitor Backlink Analysis\n\n### Key Gaps Identified\n\n1. **Beauty industry directories**: 12 major directories link to competitors, 0 to us\n2. **Eco/sustainability blogs**: 34 blogs with DA 40+ write about competitors\' sustainability claims\n3. **Retail/shopping aggregators**: 8 major comparison sites missing our brand\n\n### Opportunity Size\n- ~180 unique referring domains available through outreach\n- Estimated impact: +15-25 domain authority points over 6 months',
    links: [{ label: 'View Task', url: '/cowork?taskId=demo-2', type: 'page' }],
    sourceType: 'task', sourceId: 'demo-2',
    date: new Date().toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 5 * 3600000).toISOString(),
  },
  {
    id: 'demo-3', teamId: 'demo', agentId: 'kai-analytics',
    title: 'Q1 Performance Report: 23% Revenue Growth',
    summary: 'Q1 closed with 23% YoY revenue growth driven by influencer campaigns. ROAS improved to 4.2x. Email channel emerged as top converter at 6.8% CVR.',
    content: '## Q1 Performance Report\n\n### Executive Summary\nStrong quarter overall with **23% YoY revenue growth**: ahead of our 18% target.\n\n### Channel Breakdown\n\n| Channel | Revenue | ROAS | CVR |\n|---------|---------|------|-----|\n| Influencer | $142K | 4.8x | 3.2% |\n| Email | $98K | 8.1x | 6.8% |\n| Paid Social | $76K | 2.9x | 1.4% |\n| Organic | $54K | N/A | 4.1% |\n\n### Key Insights\n- **Influencer campaigns** drove highest volume but email has best ROAS\n- **Micro-influencer** (10K-50K followers) content outperformed macro by 2.3x engagement',
    links: [{ label: 'View Task', url: '/cowork?taskId=demo-3', type: 'page' }],
    sourceType: 'task', sourceId: 'demo-3',
    date: new Date(Date.now() - 86400000).toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 26 * 3600000).toISOString(),
  },
  {
    id: 'demo-4', teamId: 'demo', agentId: 'rex-influencer',
    title: 'Swedish Influencer List: 847 Creators Compiled',
    summary: 'Curated 847 Swedish beauty/lifestyle creators matching our criteria. Top tier: 23 creators with 100K+ followers, avg. eng. rate 4.7%.',
    content: '## Swedish Influencer Discovery: Complete\n\n### Results Summary\n- **847 total creators** identified matching brand criteria\n- **23 top-tier** (100K+ followers)\n- **156 mid-tier** (10K-100K followers)\n- **668 micro** (1K-10K followers, avg. 8.2% engagement)\n\n### Top Creator Highlights\n1. **@thordis_beauty**: 284K followers, 5.1% ER, beauty/wellness niche\n2. **@lena.stockholm**: 198K followers, 4.8% ER, lifestyle/sustainable beauty\n3. **@nordic.glam**: 156K followers, 6.2% ER, makeup/skincare reviews\n\n### Filtering Criteria Applied\n- Minimum 3.5% engagement rate\n- Swedish audience ≥60%\n- Brand-safe content\n- Active posting cadence (3+ posts/week)',
    links: [{ label: 'View Task', url: '/cowork?taskId=demo-4', type: 'page' }],
    sourceType: 'task', sourceId: 'demo-4',
    date: new Date(Date.now() - 86400000).toISOString().slice(0, 10),
    createdAt: new Date(Date.now() - 30 * 3600000).toISOString(),
  },
];

/* ─── Agent Avatar ─── */

function AgentAvatar({ agent, size = 40 }: { agent?: Agent; size?: number }) {
  if (!agent) {
    return (
      <div className="rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42 }}>
        🤖
      </div>
    );
  }
  if (agent.avatarUrl) {
    return (
      <img
        src={avatarSrc(agent)}
        alt={agent.name} className="rounded-full object-cover flex-shrink-0"
        style={{ width: size, height: size }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="rounded-full flex items-center justify-center flex-shrink-0 text-white"
      style={{ width: size, height: size, backgroundColor: agent.accentColor || '#be43ff', fontSize: size * 0.42 }}>
      {agent.avatarEmoji || '🤖'}
    </div>
  );
}

/* ─── Content renderer (simple markdown) ─── */

function renderContent(text: string) {
  const lines = text.split('\n');
  const elements: React.ReactElement[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-base font-bold text-gray-900 mt-5 mb-2">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-sm font-semibold text-gray-800 mt-4 mb-1.5">{line.slice(4)}</h3>);
    } else if (line.startsWith('- ')) {
      const bullets: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) { bullets.push(lines[i].slice(2)); i++; }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1 mb-3">
          {bullets.map((b, bi) => (
            <li key={bi} className="flex items-start gap-2 text-sm text-gray-700">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#be43ff] flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />
            </li>
          ))}
        </ul>
      );
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) { items.push(lines[i].replace(/^\d+\. /, '')); i++; }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-1 mb-3 list-decimal list-inside">
          {items.map((item, ii) => <li key={ii} className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />)}
        </ol>
      );
      continue;
    } else if (line.startsWith('| ')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!lines[i].includes('---')) rows.push(lines[i].split('|').slice(1, -1).map(c => c.trim()));
        i++;
      }
      if (rows.length > 0) {
        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto mb-3">
            <table className="w-full text-sm border-collapse">
              <thead><tr className="border-b border-gray-200">{rows[0].map((h, hi) => <th key={hi} className="text-left px-3 py-2 font-semibold text-gray-700 text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody>{rows.slice(1).map((row, ri) => <tr key={ri} className={cn('border-b border-gray-100', ri % 2 === 0 ? 'bg-gray-50/50' : '')}>{row.map((cell, ci) => <td key={ci} className="px-3 py-2 text-gray-700">{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );
      }
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-sm text-gray-700 leading-relaxed mb-2" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />);
    }
    i++;
  }
  return elements;
}

/* ─── Detail Panel ─── */

function DiaryDetail({ entry, agent, onClose }: { entry: DiaryEntry; agent?: Agent; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white z-50 flex flex-col" style={{ boxShadow: '-8px 0 40px rgba(0,0,0,0.12)' }}>
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${agent?.accentColor || '#be43ff'}12, white)` }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <AgentAvatar agent={agent} size={52} />
              <div>
                <p className="text-xs font-semibold" style={{ color: agent?.accentColor || '#be43ff' }}>{agent?.name || entry.agentId}</p>
                <p className="text-xs text-gray-500">{agent?.title || ''}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <h2 className="mt-4 text-lg font-bold text-gray-900 leading-snug">{entry.title}</h2>
          <p className="mt-1 text-xs text-gray-500">{formatFullDate(entry.createdAt)}</p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{renderContent(entry.content)}</div>

        {/* Links */}
        {entry.links.length > 0 && (
          <div className="flex-shrink-0 border-t border-gray-100 px-6 py-4 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Related</p>
            <div className="space-y-2">
              {entry.links.map((link, i) => (
                <button key={i} onClick={() => link.type === 'external' ? window.open(link.url, '_blank') : null}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white border border-gray-100 hover:border-[#be43ff]/30 transition-all text-left group">
                  {link.type === 'file' ? <File className="w-4 h-4 text-gray-400 flex-shrink-0" /> : link.type === 'external' ? <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ArrowUpRight className="w-4 h-4 text-[#be43ff] flex-shrink-0" />}
                  <span className="text-sm text-gray-700 group-hover:text-[#be43ff] transition-colors flex-1 truncate">{link.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/* ─── Card ─── */

function DiaryCard({ entry, agent, onClick }: { entry: DiaryEntry; agent?: Agent; onClick: () => void }) {
  return (
    <button onClick={onClick} className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:border-[#be43ff]/30 hover:shadow-sm transition-all group" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div className="flex items-start gap-3">
        <AgentAvatar agent={agent} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className="text-xs font-semibold truncate" style={{ color: agent?.accentColor || '#be43ff' }}>{agent?.name || entry.agentId}</span>
            <span className="text-xs text-gray-400 flex-shrink-0">{formatTime(entry.createdAt)}</span>
          </div>
          <p className="text-sm font-semibold text-gray-900 leading-snug mb-1 group-hover:text-[#be43ff] transition-colors">{entry.title}</p>
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">{entry.summary}</p>
          {entry.links.length > 0 && (
            <div className="mt-2 flex items-center gap-1 text-xs text-[#be43ff]">
              <ChevronRight className="w-3 h-3" />
              <span>{entry.links.length} link{entry.links.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

/* ─── Main Page ─── */

export function GrowthDiaryPage({ agents, onBack }: GrowthDiaryPageProps) {
  const [entries, setEntries] = useState<DiaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState<DiaryEntry | null>(null);
  const [filterAgentId, setFilterAgentId] = useState('');
  const [isDemo, setIsDemo] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/v1/diary?limit=100${filterAgentId ? `&agentId=${filterAgentId}` : ''}`;
      const res = await fetch(url, { headers: authHeaders(false) });
      if (res.ok) {
        const data: DiaryEntry[] = await res.json();
        if (data.length === 0 && !filterAgentId) {
          setEntries(DEMO_ENTRIES);
          setIsDemo(true);
        } else {
          setEntries(data);
          setIsDemo(false);
        }
      }
    } catch {
      setEntries(DEMO_ENTRIES);
      setIsDemo(true);
    } finally {
      setLoading(false);
    }
  }, [filterAgentId]);

  useEffect(() => {
    setLoading(true);
    fetchEntries();
  }, [fetchEntries]);

  const agentMap = new Map(agents.map(a => [a.id, a]));

  const grouped = entries.reduce<Record<string, DiaryEntry[]>>((acc, entry) => {
    if (!acc[entry.date]) acc[entry.date] = [];
    acc[entry.date].push(entry);
    return acc;
  }, {});

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="flex-1 flex flex-col min-w-0 relative bg-[#fafafa] rounded-tl-[24px] h-full">
      {/* Top bar */}
      <div className="h-12 shrink-0 flex items-center gap-3 px-5 border-b border-gray-100/60">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#be43ff]" />
          <span className="text-[13px] font-semibold text-[#344054]">Growth Diary</span>
        </div>
        <div className="flex-1" />
        {agents.length > 0 && (
          <select value={filterAgentId} onChange={e => setFilterAgentId(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-[#be43ff]/30">
            <option value="">All agents</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-6">
          {/* Demo notice */}
          {isDemo && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-[#be43ff]/5 border border-[#be43ff]/20 text-sm text-[#be43ff]">
              Showing sample entries: complete tasks to populate your Growth Diary automatically.
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#be43ff] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && entries.length === 0 && (
            <div className="text-center py-20">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No diary entries yet</p>
              <p className="text-sm text-gray-400 mt-1">Complete tasks to start building your Growth Diary.</p>
            </div>
          )}

          {!loading && sortedDates.map(date => (
            <div key={date} className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{formatDate(date)}</span>
                <div className="flex-1 h-px bg-gray-100" />
                <span className="text-xs text-gray-400">{grouped[date].length} entr{grouped[date].length === 1 ? 'y' : 'ies'}</span>
              </div>
              <div className="space-y-3">
                {grouped[date].map(entry => (
                  <DiaryCard key={entry.id} entry={entry} agent={agentMap.get(entry.agentId)} onClick={() => setSelectedEntry(entry)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail slide-in */}
      {selectedEntry && (
        <DiaryDetail entry={selectedEntry} agent={agentMap.get(selectedEntry.agentId)} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}
